'use client';

import { slugify } from '@/lib/utils';
import { useArticleList } from '@/services/query/article.query';
import { ArticleType } from '@/services/types/article.type';
import { useModalStore } from '@/store/modal.store';
import Link from 'next/link';
import ImageOptimizer from './image-optimizer';

// 예약 CTA 배너 이미지. 과거 본문 HTML에 <img>로 박혀 있던 것과 동일한 파일.
// 클릭 시 전역 상담 모달을 연다(원본 onclick=window.openReserveModal 재현).
const RESERVE_BANNER_SRC =
  'https://file.drivingzone.co.kr/image/2025/0625/19672-sum_03.png';

interface Props {
  // 현재 글이 속한 시험장(academy) ID. 이 시험장의 다른 코스분석 글을 노출한다.
  academyId: number;
  // 현재 글 ID. 관련글 목록에서 제외한다.
  currentId: number;
}

// 코스분석 글 하단의 "상담예약 CTA + 함께 보면 좋은 글" 동적 섹션.
// 같은 시험장(academy)의 다른 코스분석 글(도로주행·장내기능)을 API로 불러 노출한다.
// 과거엔 이 블록이 글마다 본문 HTML에 수작업으로 박혀 있었고 같은 시험장 링크만
// 일부 글에 있었는데, 이를 동적화해 전체 코스분석 글에 균일하게 노출한다.
export default function RelatedCourseArticles({ academyId, currentId }: Props) {
  const { open: openReserveModal } = useModalStore();

  const { data } = useArticleList({
    type: ArticleType.TEST_COURSE_ANALYSIS,
    academyId,
    limit: 20,
  });

  const related = (data?.data?.articles ?? []).filter(
    (a) => a.id !== currentId,
  );

  return (
    <div className="desktop:mt-16 desktop:flex desktop:justify-center mt-12">
      <div className="desktop:max-w-[750px] flex w-full flex-col">
        {/* 상담예약 CTA: 과거 본문에 이미지로 박혀 있던 예약 배너를 동적으로 재현.
            클릭 시 전역 상담 모달을 연다. 원본 이미지 크기 2250×936. */}
        <button
          type="button"
          onClick={() => openReserveModal()}
          aria-label="상담예약하기"
          className="relative block aspect-[2250/936] w-full overflow-hidden rounded-lg"
        >
          <ImageOptimizer
            src={RESERVE_BANNER_SRC}
            alt="상담예약하기"
            fill
            sizes="(max-width: 768px) 100vw, 750px"
            className="object-contain"
          />
        </button>

        {/* 함께 보면 좋은 글: 같은 시험장의 다른 코스분석 글 */}
        {related.length > 0 && (
          <div className="desktop:mt-10 mt-8">
            {/* 마무리·FAQ 제목과 동일 스케일(24px/700) */}
            <h2 className="mb-4 text-[24px] leading-[36px] font-[700]">
              함께 보면 좋은 글
            </h2>
            <ul className="flex flex-col gap-2">
              {related.map((a) => (
                <li key={a.id}>
                  <Link
                    href={`/story/test-course-analysis/${slugify(a.title, a.id)}`}
                    className="text-font-blue text-[18px] leading-[28px] underline"
                  >
                    {a.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
